/**
 * Internationalization (i18n) system for Pythagoras Trees
 * Handles language detection, translation loading, and UI updates
 */

import { domCache } from './dom-cache.js';

/**
 * Main internationalization manager
 */
export class I18nManager {
    constructor() {
        this.currentLanguage = 'en';
        this.translations = new Map();
        this.supportedLanguages = ['en', 'es', 'fr', 'de', 'it'];
        this.defaultLanguage = 'en';
        this.isInitialized = false;

        // Callbacks for language change events
        this.changeCallbacks = new Set();
    }

    /**
     * Initialize the i18n system
     */
    async initialize() {
        if (this.isInitialized) {
            return;
        }

        try {
            // Detect the appropriate language
            this.currentLanguage = this.detectLanguage();

            // Load the detected language translations
            await this.loadTranslations(this.currentLanguage);

            // Load fallback language if different from current
            if (this.currentLanguage !== this.defaultLanguage) {
                await this.loadTranslations(this.defaultLanguage);
            }

            this.isInitialized = true;
        } catch (error) {
            console.error('Failed to initialize i18n system:', error);
            // Fallback to default language
            this.currentLanguage = this.defaultLanguage;
            await this.loadTranslations(this.defaultLanguage);
            this.isInitialized = true;
        }
    }

    /**
     * Detect the appropriate language based on priority hierarchy
     */
    detectLanguage() {
        // 1. Check explicit user choice (localStorage)
        try {
            const savedLang = localStorage.getItem('pythagoras-language');
            if (savedLang && this.isLanguageSupported(savedLang)) {
                return savedLang;
            }
        } catch (e) {
            console.warn('Could not access localStorage for language detection');
        }

        // 2. Check URL parameter
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const urlLang = urlParams.get('lang');
            if (urlLang && this.isLanguageSupported(urlLang)) {
                return urlLang;
            }
        } catch (e) {
            console.warn('Could not parse URL parameters for language detection');
        }

        // 3. Check browser language preferences
        try {
            const browserLangs = navigator.languages || [
                navigator.language || navigator.userLanguage,
            ];
            for (const lang of browserLangs) {
                const normalizedLang = this.normalizeLangCode(lang);
                if (this.isLanguageSupported(normalizedLang)) {
                    return normalizedLang;
                }
            }
        } catch (e) {
            console.warn('Could not detect browser language preferences');
        }

        // 4. Default fallback        return this.defaultLanguage;
    }

    /**
     * Normalize language code (e.g., 'en-US' -> 'en')
     */
    normalizeLangCode(langCode) {
        if (!langCode) {
            return this.defaultLanguage;
        }
        return langCode.split('-')[0].toLowerCase();
    }

    /**
     * Check if a language is supported
     */
    isLanguageSupported(langCode) {
        return this.supportedLanguages.includes(langCode);
    }

    /**
     * Load translations for a specific language
     */
    async loadTranslations(langCode) {
        if (this.translations.has(langCode)) {
            return this.translations.get(langCode);
        }

        try {
            const response = await fetch(`locales/${langCode}.json`);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const translations = await response.json();
            this.translations.set(langCode, translations);
            return translations;
        } catch (error) {
            console.error(`Failed to load translations for ${langCode}:`, error);

            // If loading fails and it's not the default language, try default
            if (langCode !== this.defaultLanguage) {
                return this.loadTranslations(this.defaultLanguage);
            }

            // If even default fails, return empty object
            return {};
        }
    }

    /**
     * Get translated text for a key
     */
    t(key, params = {}) {
        const translation =
            this.getTranslation(key, this.currentLanguage) ||
            this.getTranslation(key, this.defaultLanguage) ||
            key;

        // Simple parameter interpolation
        return this.interpolate(translation, params);
    }

    /**
     * Get translation from loaded translations
     */
    getTranslation(key, langCode) {
        const translations = this.translations.get(langCode);
        if (!translations) {
            return null;
        }

        // Support nested keys like 'ui.panel.title'
        const keys = key.split('.');
        let result = translations;

        for (const k of keys) {
            if (result && typeof result === 'object' && k in result) {
                result = result[k];
            } else {
                return null;
            }
        }

        return typeof result === 'string' ? result : null;
    }

    /**
     * Simple parameter interpolation
     */
    interpolate(text, params) {
        if (!params || typeof text !== 'string') {
            return text;
        }

        return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
            return params[key] !== undefined ? params[key] : match;
        });
    }

    /**
     * Change the current language
     */
    async changeLanguage(langCode) {
        if (!this.isLanguageSupported(langCode)) {
            console.warn(`Language ${langCode} is not supported`);
            return false;
        }

        if (langCode === this.currentLanguage) {
            return true; // Already using this language
        }

        try {
            // Load translations if not already loaded
            await this.loadTranslations(langCode);

            // Update current language
            const previousLang = this.currentLanguage;
            this.currentLanguage = langCode;

            // Save to localStorage
            try {
                localStorage.setItem('pythagoras-language', langCode);
            } catch (e) {
                console.warn('Could not save language preference to localStorage');
            }

            // Notify callbacks
            this.notifyLanguageChange(langCode, previousLang);
            return true;
        } catch (error) {
            console.error(`Failed to change language to ${langCode}:`, error);
            return false;
        }
    }

    /**
     * Register a callback for language change events
     */
    onLanguageChange(callback) {
        if (typeof callback === 'function') {
            this.changeCallbacks.add(callback);
        }
    }

    /**
     * Unregister a language change callback
     */
    offLanguageChange(callback) {
        this.changeCallbacks.delete(callback);
    }

    /**
     * Notify all registered callbacks of language change
     */
    notifyLanguageChange(newLang, oldLang) {
        this.changeCallbacks.forEach(callback => {
            try {
                callback(newLang, oldLang);
            } catch (error) {
                console.error('Error in language change callback:', error);
            }
        });
    }

    /**
     * Get current language
     */
    getCurrentLanguage() {
        return this.currentLanguage;
    }

    /**
     * Get supported languages
     */
    getSupportedLanguages() {
        return [...this.supportedLanguages];
    }

    /**
     * Get language name in native script
     */
    getLanguageName(langCode) {
        const names = {
            en: 'English',
            es: 'Español',
            fr: 'Français',
            de: 'Deutsch',
            it: 'Italiano',
        };
        return names[langCode] || langCode;
    }

    /**
     * Update HTML lang attribute
     */
    updateHtmlLang() {
        try {
            document.documentElement.lang = this.currentLanguage;
        } catch (error) {
            console.warn('Could not update HTML lang attribute:', error);
        }
    }

    /**
     * Debug information
     */
    getDebugInfo() {
        return {
            currentLanguage: this.currentLanguage,
            supportedLanguages: this.supportedLanguages,
            loadedLanguages: Array.from(this.translations.keys()),
            browserLanguages: navigator.languages || [navigator.language],
            isInitialized: this.isInitialized,
        };
    }
}

// Create singleton instance
export const i18n = new I18nManager();

// Convenience function for translations
export const t = (key, params = {}) => i18n.t(key, params);

// Export for global access if needed
if (typeof window !== 'undefined') {
    window.i18n = i18n;
    window.t = t;
}
